/**
 * The two status pills of a listing row: publication and availability.
 *
 * Built with the repo's inline-pill convention (the same shape as
 * `deposits/DepositBadge`, `tenants/TenantUnitGroup` and
 * `responsibilities/ResponsibilityAreaList`) rather than `ui/badge.jsx`, which
 * has no consumers anywhere in the app. Text first, colour second: every pill
 * spells its state out, so the tone only reinforces what the words already say.
 *
 * Neither pill changes the meaning of the underlying field. `is_published` and
 * `availability_status` remain the source of truth and are still written only
 * through PATCH /listings/{id}/status.
 */
import React from "react";

import { availabilityLabel } from "./utils/listingHelpers";

const PILL =
  "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold";

const TONES = {
  positive:
    "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
  warn: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  neutral: "border-border bg-muted text-muted-foreground",
};

function Pill({ tone, children }) {
  return <span className={`${PILL} ${TONES[tone] || TONES.neutral}`}>{children}</span>;
}

/** Online when published, Offline otherwise — the wording the table used before. */
export function ListingPublishBadge({ isPublished }) {
  return (
    <Pill tone={isPublished ? "positive" : "neutral"}>
      {isPublished ? "Online" : "Offline"}
    </Pill>
  );
}

/** Verfügbar / Belegt / Nicht verfügbar, via the existing label helper. */
export function ListingAvailabilityBadge({ status }) {
  const value = (status || "available").toLowerCase();
  const tone =
    value === "occupied" ? "warn" : value === "unavailable" ? "neutral" : "positive";
  return <Pill tone={tone}>{availabilityLabel(status)}</Pill>;
}
